import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../../components/Header";
import useFetch from "../features/get";
import { useLocation } from "react-router-dom";
import HotelGallery from "../../components/Hotel/HotelGallery";
import RoomList from "../../components/Hotel/RoomList";
import HotelEquipments from "../../components/Hotel/HotelEquipments";
import HotelReviews from "../../components/Hotel/HotelReviews";
import BookingCard from "../../components/Hotel/BookingCards";
import SectionTabs from "../../components/Hotel/SectionsTabs";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { BsArrowLeft, BsStarFill, BsGeoAlt } from "react-icons/bs";
import { MdOutlineBedroomParent } from "react-icons/md";
import LandingHeader from "../../components/LandingHeader";
import HotelSEO from "../../components/Hotel/HotelSEO";



// ── Séparateur réutilisable ───────────────────────────────────────
function Divider() {
  return (
    <div className="my-12 flex items-center">
      <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
    </div>
  );
}

function Hotel() {
  const [rooms, setRooms] = useState([]);
  const { slug } = useParams();
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [prices, setPrices] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [avgRating, setAvgRating] = useState(null);
  const [totalReviews, setTotalReviews] = useState(0);

  const location = useLocation();
  const navigate = useNavigate();

  const { data, loading } = useFetch(
    `https://api.komorotel.com/api/hotels/slug/${slug}`
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ci = params.get("checkIn");
    const co = params.get("checkOut");
    if (ci && co) { setCheckIn(ci); setCheckOut(co); }
  }, [location.search]);

  useEffect(() => {
    if (!checkIn || !checkOut) return;
    const params = new URLSearchParams(location.search);
    params.set("checkIn", checkIn);
    params.set("checkOut", checkOut);
    navigate(`?${params.toString()}`, { replace: true });
  }, [checkIn, checkOut]);

  useEffect(() => {
    if (!data?._id) return;
    axios
      .get(`https://api.komorotel.com/api/rooms/hotel/${data._id}`)
      .then((res) => setRooms(res.data))
      .catch(console.log);
  }, [data]);

  useEffect(() => {
    if (!checkIn || !checkOut || !data?._id) return;
    setLoadingPrices(true);
    axios
      .get(`https://api.komorotel.com/api/rateprices/hotel/${data._id}`, {
        params: { checkIn, checkOut },
      })
      .then((res) => setPrices(res.data))
      .catch(console.log)
      .finally(() => setLoadingPrices(false));
  }, [checkIn, checkOut, data]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.pageYOffset - 100,
      behavior: "smooth",
    });
  };

  

  // ── Loading state ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-7 h-7 rounded-full border-2 border-[#3A9D23] border-t-transparent animate-spin" />
          <p className="text-sm text-gray-400 tracking-wide">Chargement...</p>
        </div>
      </div>
    );
  }


  return (
    <div className="bg-white min-h-screen">
      // Dans le JSX, tout en haut du return, avant LandingHeader :
      <HotelSEO data={data} avgRating={avgRating} totalReviews={totalReviews} />

      <LandingHeader />

      {/* ── GALERIE — pleine largeur, sans padding ───────────────── */}
      <div className="w-full md:pt-[2px]">
        <HotelGallery photos={data.photos} />
      </div>

      {/* ── WRAPPER PRINCIPAL ────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">

        {/* Breadcrumb discret */}
        <div className="hidden md:flex items-center gap-1.5 pt-5 pb-1 text-sm text-gray-400">
          <Link to="/" className="hover:text-gray-600 transition">Accueil</Link>
          <span>·</span>
          <Link
            to={`/hotels?destination=${encodeURIComponent(data?.island)}`}
            className="hover:text-gray-600 transition"
          >
            {data?.island}
          </Link>
          <span>·</span>
          <span className="text-gray-600">{data?.name}</span>
        </div>

        {/* ── HERO : nom + meta + tabs ─────────────────────────────── */}
        <div className="pt-4 pb-0">

          {/* Nom + méta */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-5">
            <div>
              <h1 className="text-3xl md:text-[2.4rem] font-semibold tracking-tight text-gray-950 leading-tight">
                {data.name}
              </h1>

              {/* Meta row */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2.5">
                <span className="flex items-center gap-1.5 text-sm text-gray-500">
                  <BsGeoAlt className="text-gray-400 shrink-0" />
                  {data.adress}, {data.city}
                </span>

                {avgRating && (
                  <span className="flex items-center gap-1.5 text-sm">
                    <BsStarFill className="text-amber-400 text-[11px]" />
                    <span className="font-semibold text-gray-900">{avgRating}</span>
                    <span className="text-gray-400">
                      · {totalReviews} avis
                    </span>
                  </span>
                )}

                {rooms.length > 0 && (
                  <span className="flex items-center gap-1.5 text-sm text-gray-400">
                    <MdOutlineBedroomParent className="text-gray-400" />
                    {rooms.length} type{rooms.length > 1 ? "s" : ""} de chambre
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Nav tabs */}
          <div className="hidden md:block">
            <SectionTabs scrollToSection={scrollToSection} />
          </div>
        </div>

        {/* ── LAYOUT : contenu + sidebar ───────────────────────────── */}
        <div className="flex flex-col lg:flex-row gap-14 pt-10">

          {/* ── COLONNE PRINCIPALE ───────────────────────────────── */}
          <div className="flex-1 min-w-0">

            {/* 1 — PRÉSENTATION */}
            <section id="presentation" className="scroll-mt-28">
              <SectionTitle>À propos</SectionTitle>

              <div className="grid md:grid-cols-5 gap-8 mt-6 items-start">
                {/* Description */}
                <div className="md:col-span-3">
                  <p className="text-[15px] text-gray-600 leading-[1.8]">
                    {data.desc}
                  </p>
                </div>

                {/* Carte */}
                <div className="md:col-span-2">
                  <div className="w-full h-[210px] rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
                    <iframe
                      title="map"
                      className="w-full h-full border-0"
                      loading="lazy"
                      src={`https://maps.google.com/maps?q=${encodeURIComponent(
                        [data.name, data.adress, data.city, "Comores"]
                          .filter(Boolean)
                          .join(", ")
                      )}&output=embed`}
                    />
                  </div>
                  {/* Adresse sous la carte */}
                  <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                    <BsGeoAlt />
                    {data.adress}, {data.city}
                  </p>
                </div>
              </div>
            </section>

            <Divider />

            {/* 2 — AVIS */}
            <section id="avis" className="scroll-mt-28">
              <HotelReviews
                hotelId={data?._id}
                onStatsLoad={(avg, total) => {
                  setAvgRating(avg);
                  setTotalReviews(total);
                }}
              />
            </section>

            <Divider />

            {/* 3 — CHAMBRES */}
            <section id="rooms" className="scroll-mt-28">

              {/* BookingCard mobile uniquement */}
              <div className="lg:hidden mb-8">
                <BookingCard
                  checkIn={checkIn}
                  checkOut={checkOut}
                  setCheckIn={setCheckIn}
                  setCheckOut={setCheckOut}
                />
              </div>

              <RoomList
                rooms={rooms}
                checkIn={checkIn}
                checkOut={checkOut}
                prices={prices}
                loadingPrices={loadingPrices}
              />
            </section>

            <Divider />

            {/* 4 — ÉQUIPEMENTS */}
            <section id="equipements" className="scroll-mt-28 pb-24">
              <HotelEquipments equipments={data.equipments} />
            </section>

          </div>

          {/* ── SIDEBAR STICKY ───────────────────────────────────── */}
          <aside className="hidden lg:block w-[320px] shrink-0">
            <div className="sticky top-[104px] flex flex-col gap-4">

              <BookingCard
                checkIn={checkIn}
                checkOut={checkOut}
                setCheckIn={setCheckIn}
                setCheckOut={setCheckOut}
              />

              {/* Badge réassurance */}
              <div className="flex flex-col gap-2 px-1">
                {[
                  "Réservation instantanée",
                  "Aucun frais de service",
                  "Annulation selon conditions",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#3A9D23] shrink-0" />
                    {item}
                  </div>
                ))}
              </div>

            </div>
          </aside>

        </div>
      </div>
    </div>
  );
}

// ── Titre de section réutilisable ────────────────────────────────
function SectionTitle({ children }) {
  return (
    <h2 className="text-xl font-semibold text-gray-900">
      {children}
    </h2>
  );
}

export default Hotel;